import React, { useState, useEffect, useCallback, useRef } from "react";
import ViewerNavbar from "./ViewerNavbar";
import API from "../../services/api.ts";
import { getMediaUrl } from "../../services/media.ts";
import "./ImageViewer.css";

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 8;
const ZOOM_STEP = 1.25;

const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

const ImageViewer = () => {
    // Get parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const studyIdParam = urlParams.get('study_id');
    const patientIdParam = urlParams.get('patient_id');
    const dataIdsParam = urlParams.get('data_ids'); // Legacy format: just IDs
    const imageDataParam = urlParams.get('image_data'); // New format: "id:source_model,id:source_model"

    const [studyId] = useState(studyIdParam || localStorage.getItem('studyId'));
    const [studyName, setStudyName] = useState("");
    const [labels, setLabels] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [scores, setScores] = useState([]);
    const [shareAnnotations, setShareAnnotations] = useState(false);
    const [images, setImages] = useState([]); // Array of {id, patient_id, description, filePath}
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [imageSrc, setImageSrc] = useState(null);
    const [imageLoadError, setImageLoadError] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [naturalSize, setNaturalSize] = useState(null);
    const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
    const imageMainRef = useRef(null);

    // Reset zoom whenever the displayed image changes.
    useEffect(() => {
        setZoom(1);
        setNaturalSize(null);
        if (imageMainRef.current) {
            imageMainRef.current.scrollTo({ left: 0, top: 0 });
        }
    }, [currentIndex]);

    // Track both axes of the scroll container so zoom=1 fits the whole image
    // on screen (not just by width). Re-runs once the conditionally-rendered
    // main panel mounts.
    useEffect(() => {
        const el = imageMainRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(([entry]) => {
            setContainerSize({
                w: entry.contentRect.width,
                h: entry.contentRect.height,
            });
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [loading, images.length]);

    // At zoom=1 the image should fit entirely inside the visible scroll area
    // (both width AND height), preserving aspect ratio. When the panel is
    // wider than tall — e.g. when the user fullscreens the viewer card — a
    // width-only fit would let the image overflow vertically and look
    // "cropped" until they scrolled. We use the smaller of the two ratios
    // (capped at 1 so small images stay at natural pixel size).
    const fitScale = naturalSize && containerSize.w && containerSize.h
        ? Math.min(
            containerSize.w / naturalSize.w,
            containerSize.h / naturalSize.h,
            1,
        )
        : null;
    const imageStyle = fitScale
        ? {
            width: `${naturalSize.w * fitScale * zoom}px`,
            height: `${naturalSize.h * fitScale * zoom}px`,
            maxWidth: 'none',
            maxHeight: 'none',
        }
        : { maxWidth: '100%', maxHeight: '100%', height: 'auto' };

    const zoomIn = useCallback(() => setZoom((z) => clampZoom(z * ZOOM_STEP)), []);
    const zoomOut = useCallback(() => setZoom((z) => clampZoom(z / ZOOM_STEP)), []);
    const zoomReset = useCallback(() => setZoom(1), []);

    // Ctrl/Cmd + wheel zooms toward the cursor; plain wheel still scrolls.
    const handleWheel = useCallback((e) => {
        if (!(e.ctrlKey || e.metaKey)) return;
        e.preventDefault();
        const container = imageMainRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const pointerX = e.clientX - rect.left + container.scrollLeft;
        const pointerY = e.clientY - rect.top + container.scrollTop;

        setZoom((prev) => {
            const next = clampZoom(prev * (e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP));
            if (next === prev) return prev;
            const ratio = next / prev;
            // Keep the pointer anchored to the same image point after scaling.
            requestAnimationFrame(() => {
                if (!container) return;
                container.scrollLeft = pointerX * ratio - (e.clientX - rect.left);
                container.scrollTop = pointerY * ratio - (e.clientY - rect.top);
            });
            return next;
        });
    }, []);

    useEffect(() => {
        const el = imageMainRef.current;
        if (!el) return;
        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [handleWheel, loading, images.length]);

    // Fetch study info including labels, questions, and scores
    const fetchStudyInfo = useCallback(async () => {
        if (!studyId) return;

        try {
            const response = await API.get(`/studies/${studyId}/`);
            setStudyName(response.data.name);
            setLabels(response.data.labels || []);
            setQuestions(response.data.questions || []);
            setScores(response.data.scores || []);
            setShareAnnotations(response.data.share_annotations || false);
        } catch (err) {
            console.error("Error fetching study info:", err);
        }
    }, [studyId]);

    // Fetch images based on data IDs and resolve actual file paths
    // Handles both MedicalImage and PatientData objects
    const fetchImages = useCallback(async () => {
        const hasImageData = imageDataParam || dataIdsParam;
        if (!hasImageData) {
            setLoading(false);
            return;
        }

        // Parse image items - support new format with source model
        let imageItems = [];

        if (imageDataParam) {
            // New format: "id:source_model,id:source_model"
            imageItems = imageDataParam.split(',').map(item => {
                const [id, source] = item.split(':');
                return { id: id.trim(), source: source?.trim() || 'MedicalImage' };
            }).filter(item => item.id);
        } else if (dataIdsParam) {
            // Legacy format: just IDs, will try MedicalImage first then PatientData
            imageItems = dataIdsParam.split(',').map(id => ({
                id: id.trim(),
                source: null // null means try both
            })).filter(item => item.id);
        }

        if (imageItems.length === 0) {
            setLoading(false);
            return;
        }

        try {
            const allImages = [];

            for (const imageItem of imageItems) {
                try {
                    if (imageItem.source === 'PatientData') {
                        // Directly fetch from PatientData
                        const patientDataResponse = await API.get(`/data_manager/patient-data/${imageItem.id}/`);
                        const patientData = patientDataResponse.data;

                        // Only add if it's an image type
                        if (patientData.data_type === 'image' || patientData.data_type === 'imaging') {
                            allImages.push({
                                id: patientData.id,
                                patient_id: patientData.patient_id || patientData.patient?.patient_id,
                                description: patientData.description,
                                filePath: patientData.file || patientData.location,
                                fileName: patientData.filename || 'Unknown',
                                sourceModel: 'PatientData'
                            });
                        }
                    } else if (imageItem.source === 'MedicalImage' || imageItem.source === null) {
                        // Try MedicalImage first
                        try {
                            const imageResponse = await API.get(`/data_manager/medical-images/${imageItem.id}/`);
                            const imageData = imageResponse.data;

                            // Get actual file paths
                            const filesResponse = await API.get(`/data_manager/medical-images/${imageItem.id}/files/`);
                            const filesData = filesResponse.data;

                            if (filesData.files && filesData.files.length > 0) {
                                // Add each file as a separate viewable image
                                filesData.files.forEach(file => {
                                    allImages.push({
                                        id: imageData.id,
                                        patient_id: imageData.patient_id,
                                        description: imageData.description,
                                        filePath: file.path,
                                        fileName: file.name,
                                        sourceModel: 'MedicalImage'
                                    });
                                });
                            } else {
                                // Fallback: try using the image path directly
                                allImages.push({
                                    id: imageData.id,
                                    patient_id: imageData.patient_id,
                                    description: imageData.description,
                                    filePath: imageData.image,
                                    fileName: imageData.image ? imageData.image.split('/').pop() : 'Unknown',
                                    sourceModel: 'MedicalImage'
                                });
                            }
                        } catch (err) {
                            // If MedicalImage not found and source was null, try PatientData
                            if (imageItem.source === null) {
                                console.log(`MedicalImage ${imageItem.id} not found, trying PatientData...`);
                                try {
                                    const patientDataResponse = await API.get(`/data_manager/patient-data/${imageItem.id}/`);
                                    const patientData = patientDataResponse.data;

                                    // Only add if it's an image type
                                    if (patientData.data_type === 'image' || patientData.data_type === 'imaging') {
                                        allImages.push({
                                            id: patientData.id,
                                            patient_id: patientData.patient_id || patientData.patient?.patient_id,
                                            description: patientData.description,
                                            filePath: patientData.file || patientData.location,
                                            fileName: patientData.filename || 'Unknown',
                                            sourceModel: 'PatientData'
                                        });
                                    }
                                } catch (patientDataErr) {
                                    console.error(`Error fetching data ${imageItem.id}:`, patientDataErr);
                                }
                            } else {
                                console.error(`Error fetching MedicalImage ${imageItem.id}:`, err);
                            }
                        }
                    }
                } catch (err) {
                    console.error(`Error fetching image ${imageItem.id}:`, err);
                }
            }

            setImages(allImages);
        } catch (err) {
            console.error("Error fetching images:", err);
        } finally {
            setLoading(false);
        }
    }, [imageDataParam, dataIdsParam]);

    useEffect(() => {
        fetchStudyInfo();
        fetchImages();
    }, [fetchStudyInfo, fetchImages]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            // Ignore if typing in input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
                setCurrentIndex(prev => Math.max(0, prev - 1));
            } else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
                setCurrentIndex(prev => Math.min(images.length - 1, prev + 1));
            } else if (e.key === '+' || e.key === '=') {
                zoomIn();
            } else if (e.key === '-' || e.key === '_') {
                zoomOut();
            } else if (e.key === '0') {
                zoomReset();
            }

            // Handle label shortcuts
            const key = e.key.toUpperCase();
            const matchingLabel = labels.find(l => l.shortcut === key);
            if (matchingLabel) {
                console.log(`Label "${matchingLabel.name}" activated via shortcut ${key}`);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [labels, images.length, zoomIn, zoomOut, zoomReset]);

    const currentImage = images[currentIndex];

    // Resolve the <img src> for the current image.
    //
    // PatientData files (uploaded via CLI sync) are stored with absolute
    // /data/private/... paths. Building a /media/<...> URL from those double-
    // prefixes nginx's alias and 404s. Instead, fetch the binary through the
    // authenticated /patient-data/:id/file/ endpoint and use an object URL.
    //
    // MedicalImage files use relative MEDIA_ROOT paths, so the legacy
    // getMediaUrl path still works for them.
    useEffect(() => {
        let cancelled = false;
        let objectUrl = null;
        setImageLoadError(false);

        const resolve = async () => {
            if (!currentImage) {
                setImageSrc(null);
                return;
            }
            if (currentImage.sourceModel === 'PatientData') {
                try {
                    const response = await API.get(
                        `/data_manager/patient-data/${currentImage.id}/file/`,
                        { responseType: 'blob' }
                    );
                    if (cancelled) return;
                    objectUrl = URL.createObjectURL(response.data);
                    setImageSrc(objectUrl);
                } catch (err) {
                    console.error("Failed to load PatientData image:", err);
                    if (!cancelled) {
                        setImageSrc(null);
                        setImageLoadError(true);
                    }
                }
            } else {
                setImageSrc(currentImage.filePath ? getMediaUrl(currentImage.filePath) : null);
            }
        };
        resolve();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [currentImage]);

    // Build available data types and series for annotation level filtering
    const getAvailableDataTypes = () => {
        if (images.length > 0) return ['image'];
        return [];
    };

    const getAvailableSeries = () => {
        const series = [];
        const seenIds = new Set();

        images.forEach((image) => {
            const id = `image-${image.id}`;
            if (!seenIds.has(id)) {
                seenIds.add(id);
                series.push({
                    id,
                    name: image.fileName || image.filename || `Image ${image.id}`,
                    dataType: 'image'
                });
            }
        });

        return series;
    };

    return (
        <div className="image-viewer-page">
            <ViewerNavbar
                studyId={studyId}
                studyName={studyName}
                labels={labels}
                questions={questions}
                scores={scores}
                patientId={patientIdParam || currentImage?.patient_id}
                shareAnnotations={shareAnnotations}
                availableDataTypes={getAvailableDataTypes()}
                availableSeries={getAvailableSeries()}
            />

            <div className="image-viewer-content">
                {loading ? (
                    <div className="image-viewer-loading">
                        <div className="spinner"></div>
                        <p>Loading images...</p>
                    </div>
                ) : images.length === 0 ? (
                    <div className="image-viewer-empty">
                        <i className="fa fa-image"></i>
                        <p>No images to display</p>
                        <small>Select images from the study dashboard to view them here.</small>
                    </div>
                ) : (
                    <>
                        <div className="image-viewer-main" ref={imageMainRef}>
                            <div className="image-container">
                                {imageSrc && !imageLoadError ? (
                                    <img
                                        src={imageSrc}
                                        alt={currentImage.description || currentImage.fileName || 'Medical Image'}
                                        className="viewer-image"
                                        style={imageStyle}
                                        onLoad={(e) => setNaturalSize({
                                            w: e.target.naturalWidth,
                                            h: e.target.naturalHeight,
                                        })}
                                        onError={() => setImageLoadError(true)}
                                        draggable={false}
                                    />
                                ) : (
                                    <div className="no-preview">
                                        <i className="fa fa-file-image-o"></i>
                                        <p>{imageLoadError ? 'Failed to load image' : 'Preview not available'}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Image Navigation — compact: arrows + counter + zoom controls.
                            Patient ID / filename / description are intentionally
                            omitted because that band ate too much vertical space
                            and hid the bottom of tall figures. */}
                        <div className="image-viewer-nav">
                            {images.length > 1 && (
                                <div className="nav-group">
                                    <button
                                        className="nav-btn prev"
                                        onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
                                        disabled={currentIndex === 0}
                                        aria-label="Previous image"
                                    >
                                        <i className="fa fa-chevron-left"></i>
                                    </button>

                                    <span className="image-counter">
                                        {currentIndex + 1} / {images.length}
                                    </span>

                                    <button
                                        className="nav-btn next"
                                        onClick={() => setCurrentIndex(prev => Math.min(images.length - 1, prev + 1))}
                                        disabled={currentIndex === images.length - 1}
                                        aria-label="Next image"
                                    >
                                        <i className="fa fa-chevron-right"></i>
                                    </button>
                                </div>
                            )}

                            <div className="nav-group">
                                <button
                                    className="nav-btn"
                                    onClick={zoomOut}
                                    disabled={zoom <= ZOOM_MIN + 1e-6}
                                    aria-label="Zoom out"
                                    title="Zoom out ( - )"
                                >
                                    <i className="fa fa-search-minus"></i>
                                </button>
                                <button
                                    className="zoom-level"
                                    onClick={zoomReset}
                                    aria-label="Reset zoom"
                                    title="Reset zoom ( 0 )"
                                >
                                    {Math.round(zoom * 100)}%
                                </button>
                                <button
                                    className="nav-btn"
                                    onClick={zoomIn}
                                    disabled={zoom >= ZOOM_MAX - 1e-6}
                                    aria-label="Zoom in"
                                    title="Zoom in ( + )"
                                >
                                    <i className="fa fa-search-plus"></i>
                                </button>
                            </div>
                        </div>

                    </>
                )}
            </div>
        </div>
    );
};

export default ImageViewer;
